import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';

const AuthContext = createContext({})

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [userProfile, setUserProfile] = useState(null)
  const [orgMembership, setOrgMembership] = useState(null)
  const [rolePermissions, setRolePermissions] = useState(null)
  const [loading, setLoading] = useState(true)
  const [profileLoading, setProfileLoading] = useState(false)

  const profileOperations = {
    async load(userId) {
      if (!userId) return
      setProfileLoading(true)
      try {
        // Load user profile (platform role)
        const { data: profile } = await supabase
          ?.from('user_profiles')
          ?.select('*')
          ?.eq('id', userId)
          ?.single()

        if (profile) setUserProfile(profile)

        // Load org membership
        const { data: membership } = await supabase
          ?.from('org_members')
          ?.select('*, organizations(id, name, slug, logo_url, primary_color)')
          ?.eq('user_id', userId)
          ?.eq('is_active', true)
          ?.single()

        if (membership) {
          setOrgMembership(membership)

          // Load role permissions for this org+role
          const { data: permissions } = await supabase
            ?.from('role_permissions')
            ?.select('*')
            ?.eq('organization_id', membership?.organization_id)
            ?.eq('role', membership?.role)
            ?.single()

          if (permissions) setRolePermissions(permissions)
        }
      } catch (error) {
        console.error('Profile load error:', error)
      } finally {
        setProfileLoading(false)
      }
    },

    clear() {
      setUserProfile(null)
      setOrgMembership(null)
      setRolePermissions(null)
      setProfileLoading(false)
    }
  }

  const authStateHandlers = {
    onChange: (event, session) => {
      setUser(session?.user ?? null)
      setLoading(false)

      if (session?.user) {
        profileOperations?.load(session?.user?.id)
      } else {
        profileOperations?.clear()
      }
    }
  }

  useEffect(() => {
    supabase?.auth?.getSession()?.then(({ data: { session } }) => {
      authStateHandlers?.onChange(null, session)
    })

    const { data: { subscription } } = supabase?.auth?.onAuthStateChange(
      authStateHandlers?.onChange
    )

    return () => subscription?.unsubscribe()
  }, [])

  const signIn = async (email, password) => {
    try {
      const { data, error } = await supabase?.auth?.signInWithPassword({ email, password })
      return { data, error }
    } catch (error) {
      return { error: { message: 'Network error. Please try again.' } }
    }
  }

  const signOut = async () => {
    try {
      const { error } = await supabase?.auth?.signOut()
      if (!error) {
        setUser(null)
        profileOperations?.clear()
      }
      return { error }
    } catch (error) {
      return { error: { message: 'Network error. Please try again.' } }
    }
  }

  const updateProfile = async (updates) => {
    if (!user) return { error: { message: 'No user logged in' } }
    try {
      const { data, error } = await supabase
        ?.from('user_profiles')
        ?.update(updates)
        ?.eq('id', user?.id)
        ?.select()
        ?.single()
      if (!error) setUserProfile(data)
      return { data, error }
    } catch (error) {
      return { error: { message: 'Network error. Please try again.' } }
    }
  }

  // Derived role helpers
  const isSuperAdmin = userProfile?.platform_role === 'superadmin'
  const isOrgAdmin = ['owner', 'admin']?.includes(orgMembership?.role)
  const orgRole = orgMembership?.role ?? null
  const organizationId = orgMembership?.organization_id ?? null
  const organization = orgMembership?.organizations ?? null

  // Permission checker - superadmin and org owner/admin bypass permission checks
  const hasPermission = (permissionKey) => {
    if (isSuperAdmin) return true
    if (isOrgAdmin) return true
    return rolePermissions?.[permissionKey] === true
  }

  // Menu visibility helpers
  const canViewMenu = (menuKey) => {
    // While profile is still loading, show menus to avoid flash of empty nav
    if (profileLoading) return true
    // If user is authenticated but profile hasn't loaded yet, show menus
    if (user && !userProfile && !orgMembership) return true
    if (isSuperAdmin) return true
    if (isOrgAdmin) return true
    const permMap = {
      dashboard: 'can_view_dashboard',
      myDashboard: 'can_view_my_dashboard',
      sales: 'can_view_sales',
      operations: 'can_view_operations',
      finance: 'can_view_finance',
      reports: 'can_view_reports',
      dataManagement: 'can_view_data_management',
      settings: 'can_view_settings',
      securityAudit: 'can_view_settings',
    }
    const key = permMap?.[menuKey]
    if (!key) return false
    return rolePermissions?.[key] === true
  }

  // Promote current user to superadmin (for use in superadmin setup)
  const promoteSuperAdmin = async (email) => {
    try {
      const { data, error } = await supabase?.rpc('set_superadmin_by_email', { target_email: email })
      if (error) return { error }
      // Reload profile
      if (user) await profileOperations?.load(user?.id)
      return { data }
    } catch (error) {
      return { error: { message: 'Network error.' } }
    }
  }

  const value = {
    user,
    userProfile,
    orgMembership,
    rolePermissions,
    loading,
    profileLoading,
    signIn,
    signOut,
    updateProfile,
    promoteSuperAdmin,
    isAuthenticated: !!user,
    isSuperAdmin,
    isOrgAdmin,
    orgRole,
    organizationId,
    organization,
    hasPermission,
    canViewMenu,
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
